import {useCallback, useState} from 'react'
import {EditModeButtons} from '../../../calendar/components/EditModeButtons.tsx'
import type {UpdateGitHubRepositoryRequest} from '@app/api-resolver/router'
import {FormFields} from './FormFields.tsx'
import {FormValues} from './FormValues.ts'
import type {GitHubRepository} from '@package/shared'
import {useForm} from '@mantine/form'
import {useConnections} from '../../../../providers/connections/useConnections.ts'
import {zod4Resolver} from 'mantine-form-zod-resolver'

type Props = {
  repository: GitHubRepository
  onSuccess: () => void
  disabled: boolean
}

export const UpdateForm = ({repository, onSuccess, disabled: containerDisabled}: Props) => {
  const {updateRepository, disableRepository, enableRepository} = useConnections()
  const [updating, setUpdating] = useState<boolean>(false)
  const [archiving, setArchiving] = useState<boolean>(false)

  const form = useForm<FormValues>({
    initialValues: repository satisfies FormValues,
    validate: zod4Resolver(FormValues),
  })

  // update
  const handleSubmit = useCallback(
    async (formValues: FormValues) => {
      setUpdating(true)
      await updateRepository({
        ...repository,
        ...formValues,
      } satisfies UpdateGitHubRepositoryRequest).finally(() => setUpdating(false))
      onSuccess()
    },
    [onSuccess, repository, updateRepository],
  )

  // disable
  const handleDisable = useCallback(async () => {
    setArchiving(true)
    await disableRepository(repository.connectionId, repository.id).finally(() => setArchiving(false))
    onSuccess()
  }, [disableRepository, repository.connectionId, repository.id, onSuccess])

  // enable
  const handleEnable = useCallback(async () => {
    setUpdating(true)
    await enableRepository(repository.connectionId, repository.id).finally(() => setUpdating(false))
    onSuccess()
  }, [enableRepository, repository.connectionId, repository.id, onSuccess])

  const disabled = containerDisabled
  const repositoryDisabled = !!repository.disabledTime

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
      <form onSubmit={form.onSubmit(handleSubmit)} style={{display: 'flex', flexDirection: 'column', gap: 10}}>
        <FormFields form={form} disabled={disabled || repositoryDisabled} />
        {!repositoryDisabled && (
          <EditModeButtons
            submitVariant={'update'}
            submitting={updating}
            destructiveVariant={'ignore'}
            onDestruct={handleDisable}
            destructing={archiving}
            withDestructiveLabel
            disabled={disabled}
          />
        )}
      </form>
      {repositoryDisabled && (
        <EditModeButtons submitVariant={'unignore'} onSubmit={handleEnable} submitting={updating} disabled={disabled} />
      )}
    </div>
  )
}
